import {gql} from '@apollo/client';
import {createStyles} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {ReviewStatus, StudyPhase} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import SearchInput from 'search/inputs/SearchInput';
import {FilterKey, Operator, StandardFilter} from 'search/SearchConstants';
import {
  CreateModalStudyQuery,
  CreateModalStudyQueryVariables,
} from 'studies/sidepanel/__generated/CreateModalStudyQuery';
import {CREATE_MODAL_STUDIES_QUERY} from 'studies/sidepanel/CreateStudyModal';
import {DesignEntryInformation} from 'studies/StudyDAO';
import {
  CustomStudyDesignEntryReviewStatusQuery,
  CustomStudyDesignEntryReviewStatusQueryVariables,
} from 'studies/typed/custom/__generated/CustomStudyDesignEntryReviewStatusQuery';
import {
  renderDesignEntryMetadataSection,
  useCustomStudyCreationState,
} from 'studies/typed/custom/CustomStudyAdapter';
import {StudySchema} from 'studies/typed/utils/TypedStudyTypes';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {Header2} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';

enum DesignEntrySelectionMode {
  CREATE_NEW = 'createNew',
  SELECT_EXISTING = 'selectExisting',
}

export const SELECTED_ENTRY_REVIEW_STATUS_QUERY = gql`
  query CustomStudyDesignEntryReviewStatusQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    entry(id: $id) {
      ... on EntryNode {
        id
        review {
          id
          status
        }
      }
    }
  }
`;

export interface CustomStudyDesignEntryModalProps {
  studySchemaId: string;
  studyPhase: StudyPhase;
  isDesignApprovalRequired: boolean;
  initialEntryFolderId?: string | null;
  title: string;
  confirmLabel: string;
  onRequestClose: () => void;
  onSubmit: (params: {
    designEntryId?: string;
    entryInformation?: DesignEntryInformation;
    phase?: StudyPhase;
  }) => Promise<void>;
}

interface CustomStudyDesignEntryModalContentsProps extends Omit<
  CustomStudyDesignEntryModalProps,
  'studySchemaId'
> {
  studySchema: StudySchema;
}

function CustomStudyDesignEntryModalContents({
  studySchema,
  studyPhase,
  isDesignApprovalRequired,
  initialEntryFolderId,
  title,
  confirmLabel,
  onRequestClose,
  onSubmit,
}: CustomStudyDesignEntryModalContentsProps): JSX.Element {
  const {classes} = useStyles();
  const [selectionMode, setSelectionMode] = useState<DesignEntrySelectionMode>(
    DesignEntrySelectionMode.CREATE_NEW
  );
  const [selectedEntryIds, setSelectedEntryIds] = useState<Array<string>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedEntryId =
    selectionMode === DesignEntrySelectionMode.SELECT_EXISTING && selectedEntryIds.length === 1
      ? selectedEntryIds[0]
      : null;
  const selectedEntryReviewResult = useSkippableBenchlingQuery<
    CustomStudyDesignEntryReviewStatusQuery,
    CustomStudyDesignEntryReviewStatusQueryVariables
  >(SELECTED_ENTRY_REVIEW_STATUS_QUERY, {
    variables: {id: selectedEntryId ?? ''},
    skip: selectedEntryId == null,
  });

  const typedStudyState = useCustomStudyCreationState(studySchema, {
    initialDesignEntryFolderId: initialEntryFolderId,
  });

  const selectedExistingEntryWouldInvalidateApproval = useMemo((): boolean => {
    if (selectedEntryId == null) {
      return false;
    }
    // Treat a review lookup we can't positively confirm as ACCEPTED (still loading, failed, or
    // pointing at data we can't read) as invalidating approval. This avoids silently skipping the
    // required design-phase transition for an entry that later turns out to be unapproved.
    if (selectedEntryReviewResult.type !== QueryResultType.SUCCESS) {
      return true;
    }
    const entry = selectedEntryReviewResult.data.entry;
    if (entry == null || isInaccessible(entry) || entry.__typename !== 'EntryNode') {
      return true;
    }
    return (entry.review?.status ?? null) !== ReviewStatus.ACCEPTED;
  }, [selectedEntryId, selectedEntryReviewResult]);

  const willReturnToDesign =
    studyPhase === StudyPhase.EXECUTION &&
    isDesignApprovalRequired &&
    (selectionMode === DesignEntrySelectionMode.CREATE_NEW || selectedExistingEntryWouldInvalidateApproval);

  const isCreateSubmitDisabled =
    selectionMode === DesignEntrySelectionMode.CREATE_NEW && typedStudyState.experimentParam == null;
  const isExistingSubmitDisabled =
    selectionMode === DesignEntrySelectionMode.SELECT_EXISTING && selectedEntryIds.length !== 1;

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      // Backend validation requires this update to explicitly move the study back to the design
      // phase whenever the change would otherwise leave an execution-phase study without an
      // approved design; see benchling/studies/domain/study_handler.py:_validate_design_entry_update.
      const phase = willReturnToDesign ? StudyPhase.DESIGN : undefined;
      if (selectionMode === DesignEntrySelectionMode.CREATE_NEW) {
        if (typedStudyState.experimentParam == null) {
          return;
        }
        if (typedStudyState.experimentParam.folder != null) {
          FolderDefaults.set(FolderDefaultsKey.NOTEBOOK, typedStudyState.experimentParam.folder);
        }
        await onSubmit({entryInformation: typedStudyState.experimentParam, phase});
      } else {
        if (selectedEntryId == null) {
          return;
        }
        await onSubmit({designEntryId: selectedEntryId, phase});
      }
      onRequestClose();
    } finally {
      // Rejected onSubmit promises are server request errors; App's unhandledrejection handler
      // surfaces those via notifications.notifyError while the modal stays open.
      setIsSubmitting(false);
    }
  };

  const buttons: Array<ModalButton> = [
    {cancel: true, text: 'Cancel', onClick: onRequestClose},
    {
      submit: true,
      text: confirmLabel,
      style: ButtonStyle.PRIMARY,
      disabled: isSubmitting || isCreateSubmitDisabled || isExistingSubmitDisabled,
    },
  ];

  const isFillInEntryFilter: StandardFilter = {
    key: FilterKey.IS_FILL_IN_ENTRY,
    operator: Operator.IS_FALSE,
    value: null,
  };

  return (
    <Modal title={title} onRequestClose={onRequestClose} buttons={buttons} onRequestSubmit={handleSubmit}>
      {willReturnToDesign && (
        <p>
          This change will move the study back to the design phase because the study design will no longer be
          approved for execution.
        </p>
      )}
      <ToggleGroup<DesignEntrySelectionMode>
        onChangeSelection={setSelectionMode}
        selection={selectionMode}
        size={ButtonSize.MEDIUM}
        options={[
          {
            id: DesignEntrySelectionMode.CREATE_NEW,
            buttonProps: {text: 'Create new entry'},
          },
          {
            id: DesignEntrySelectionMode.SELECT_EXISTING,
            buttonProps: {text: 'Select existing entry'},
          },
        ]}
      />
      {selectionMode === DesignEntrySelectionMode.CREATE_NEW ? (
        renderDesignEntryMetadataSection(typedStudyState, {includeNoneOption: false})
      ) : (
        <div>
          <Row className={classes.sectionHeader}>
            <Col sm={12}>
              <Header2>Select an entry</Header2>
            </Col>
          </Row>
          <SearchInput
            additionalFilters={[isFillInEntryFilter]}
            isMulti={false}
            onRemove={() => {
              setSelectedEntryIds([]);
            }}
            onSelect={(entryId: string) => {
              setSelectedEntryIds([entryId]);
            }}
            selected={selectedEntryIds}
            types={[ModelTypeConstants.ENTRY]}
            placeholder="Search for an entry"
          />
        </div>
      )}
    </Modal>
  );
}

export default function CustomStudyDesignEntryModal({
  studySchemaId,
  studyPhase,
  isDesignApprovalRequired,
  initialEntryFolderId,
  title,
  confirmLabel,
  onRequestClose,
  onSubmit,
}: CustomStudyDesignEntryModalProps): JSX.Element {
  const schemaResult = useBenchlingQuery<CreateModalStudyQuery, CreateModalStudyQueryVariables>(
    CREATE_MODAL_STUDIES_QUERY,
    {
      variables: {id: studySchemaId},
      fetchPolicy: 'network-only',
    }
  );

  const studySchema = useMemo((): StudySchema | null => {
    if (schemaResult.type !== QueryResultType.SUCCESS || isInaccessible(schemaResult.data.studySchema)) {
      return null;
    }
    return schemaResult.data.studySchema;
  }, [schemaResult]);

  if (schemaResult.type === QueryResultType.PENDING || studySchema == null) {
    return (
      <Modal
        title={title}
        onRequestClose={onRequestClose}
        buttons={[{cancel: true, text: 'Cancel', onClick: onRequestClose}]}
      >
        {schemaResult.type === QueryResultType.PENDING ? (
          <CenteredHorizontalThrobber />
        ) : (
          <p>Unable to load study schema.</p>
        )}
      </Modal>
    );
  }

  return (
    <CustomStudyDesignEntryModalContents
      studySchema={studySchema}
      studyPhase={studyPhase}
      isDesignApprovalRequired={isDesignApprovalRequired}
      initialEntryFolderId={initialEntryFolderId}
      title={title}
      confirmLabel={confirmLabel}
      onRequestClose={onRequestClose}
      onSubmit={onSubmit}
    />
  );
}

const useStyles = createStyles((theme) => ({
  sectionHeader: {
    paddingTop: theme.spacing.md,
  },
}));
